// reset-password.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from '../../service/utility.service';
import { NotificationService } from '../../service/notification.service';
import { LoadingService } from '../../service/loading.service';
import { AuthenticationService } from '../../service/authentication.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './vendor-reset-password.component.html',
  styleUrl: './vendor-reset-password.component.css',
})
export class VendorResetPasswordComponent implements OnInit {
  resetForm: FormGroup;
  submitted = false;
  email: string;
  token: string;
  clientId: string;
  tokenExpired = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private utilityService: UtilityService,
    private notificationService: NotificationService,
    private spinner: LoadingService,
    private authenticationService: AuthenticationService,
  ) {
    this.resetForm = this.formBuilder.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      {
        validator: this.mustMatch('password', 'confirmPassword'),
      },
    );
  }

  ngOnInit() {
    // Get query parameters
    this.route.queryParams.subscribe((params) => {
      this.email = params['email'];
      this.token = params['token'];
      this.clientId = params['clientId'];

      // Validate token
      this.validateToken();
    });
  }

  validateToken() {
    this.spinner.OnLoading.emit(true);
    this.authenticationService
      .validateResetToken(this.email, this.token, this.clientId)
      .subscribe(
        () => {
          this.spinner.OnLoading.emit(false);
        },
        (_error) => {
          this.spinner.OnLoading.emit(false);
          this.tokenExpired = true;
          this.notificationService.showError(
            'This password reset link has expired or is invalid.',
            'Error',
          );
        },
      );
  }

  onSubmit() {
    this.submitted = true;

    if (this.resetForm.invalid) {
      this.utilityService.getFormValidationErrors(this.resetForm);
      return;
    }

    this.spinner.OnLoading.emit(true);

    const request = {
      email: this.email,
      token: this.token,
      clientId: this.clientId,
      password: this.resetForm.value.password,
    };

    this.authenticationService.resetPassword(request).subscribe(
      () => {
        this.spinner.OnLoading.emit(false);
        this.notificationService.showSuccess(
          'Your password has been reset successfully. You can now log in with your new password.',
          'Success',
        );
        this.router.navigate(['/login']);
      },
      (error) => {
        this.spinner.OnLoading.emit(false);
        this.notificationService.showError(
          'Failed to reset password. Please try again.',
          'Error',
        );
      },
    );
  }

  mustMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  get f() {
    return this.resetForm.controls;
  }
}
